import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'

// ─── Tipos ────────────────────────────────────────────────────────────────────
export type ConsentState = {
  analytics:   boolean
  performance: boolean
  marketing:   boolean
}

// ─── Constantes ───────────────────────────────────────────────────────────────
const STORAGE_KEY     = 'essencia_cookie_consent'
const CONSENT_VERSION = '1.0'
const CONSENT_EVENT   = 'essencia:cookie-changed'

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useCookieConsent() {
  const [showBanner, setShowBanner]           = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [consent, setConsent]                 = useState<ConsentState>({ analytics: false, performance: false, marketing: false })
  const [initialized, setInitialized]         = useState(false)

  // ── Lê localStorage e decide se exibe o banner ───────────────────────────
  const checkExistingConsent = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (!stored) {
        setShowBanner(true)
        setInitialized(true)
        return
      }

      const parsed = JSON.parse(stored)

      if (parsed.version !== CONSENT_VERSION) {
        // Versão do termo mudou — pedir novo consentimento
        setShowBanner(true)
      } else {
        setConsent(parsed.preferences)
        setShowBanner(false)
      }
    } catch {
      setShowBanner(true)
    } finally {
      setInitialized(true)
    }
  }, [])

  // ── Inicializa e escuta eventos de outros componentes ────────────────────
  useEffect(() => {
    checkExistingConsent()

    // Sincronizar entre instâncias (ex.: Footer revoga → Banner reaparece)
    window.addEventListener(CONSENT_EVENT, checkExistingConsent)
    return () => window.removeEventListener(CONSENT_EVENT, checkExistingConsent)
  }, [checkExistingConsent])

  // ── Persiste consentimento (localStorage + Supabase) ────────────────────
  const savePreferences = useCallback(async (prefs: ConsentState) => {
    // 1. localStorage — acesso rápido para próximas visitas
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version:      CONSENT_VERSION,
      preferences:  prefs,
      consented_at: new Date().toISOString(),
    }))

    // 2. Supabase — evidência legal com timestamp
    try {
      const { data: { user } } = await supabase.auth.getUser()
      await supabase.from('cookie_consents').insert({
        user_id:      user?.id ?? null,
        session_id:   crypto.randomUUID(),
        analytics:    prefs.analytics,
        performance:  prefs.performance,
        marketing:    prefs.marketing,
        user_agent:   navigator.userAgent,
        term_version: CONSENT_VERSION,
      })
    } catch {
      // Falha silenciosa — localStorage já garante UX correta
    }

    setConsent(prefs)
    setShowBanner(false)
    setShowPreferences(false)

    // Notificar outras instâncias do hook
    window.dispatchEvent(new Event(CONSENT_EVENT))
  }, [])

  const acceptAll = useCallback(() =>
    savePreferences({ analytics: true, performance: true, marketing: true }),
  [savePreferences])

  const rejectAll = useCallback(() =>
    savePreferences({ analytics: false, performance: false, marketing: false }),
  [savePreferences])

  const openPreferences = useCallback(() => setShowPreferences(true), [])

  // ── Revoga consentimento (link "Gerenciar cookies") ──────────────────────
  const revokeConsent = useCallback(async () => {
    localStorage.removeItem(STORAGE_KEY)

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        await supabase
          .from('cookie_consents')
          .update({ revoked_at: new Date().toISOString() })
          .eq('user_id', user.id)
          .is('revoked_at', null)
      }
    } catch {
      // Falha silenciosa
    }

    setConsent({ analytics: false, performance: false, marketing: false })
    setShowBanner(true)

    // Notificar outras instâncias (ex.: CookieBanner recebe e reaparece)
    window.dispatchEvent(new Event(CONSENT_EVENT))
  }, [])

  return {
    initialized,
    showBanner,
    showPreferences,
    setShowPreferences,
    consent,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    revokeConsent,
  }
}
